import { useEffect, useState, type FormEvent } from "react";
import { api, ApiError } from "../lib/api";
import { ROLE_HINT, ROLE_LABEL } from "../lib/access";
import { Mark, SpaceDot } from "../components/primitives";

interface Preview {
  valid: boolean;
  role?: string;
  invitedBy?: string;
  expiresAt?: string;
  projects?: { name: string; color: string; prefix: string; role: string }[];
}

/** Where an invite link lands. Signing in accepts the invite. */
export function InviteAccept({ token, onSignedIn }: { token: string; onSignedIn: () => void }) {
  const [preview, setPreview] = useState<Preview | null>(null);
  const [config, setConfig] = useState<{ devLogin: boolean; roblox: boolean } | null>(null);
  const [handle, setHandle] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    document.title = "You're invited · melo.build";
    void fetch(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then(setPreview, () => setPreview({ valid: false }));
    void fetch("/auth/config")
      .then((r) => r.json())
      .then(setConfig, () => {});
  }, [token]);

  const devAccept = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post("/auth/dev", { handle, name: name || undefined, invite: token });
      history.replaceState(null, "", "/");
      onSignedIn();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not accept the invite.");
    }
  };

  return (
    <main className="signin">
      <div className="signin-card">
        <Mark />
        {!preview ? (
          <p className="muted">Checking your invite…</p>
        ) : !preview.valid ? (
          <>
            <h1>Invite expired</h1>
            <p>This link was already used or has expired. Ask whoever sent it for a new one.</p>
          </>
        ) : (
          <>
            <div>
              <h1>You're invited</h1>
              <p style={{ marginTop: 8 }}>
                {preview.invitedBy} invited you to melo.build as {ROLE_LABEL[preview.role!]?.toLowerCase()}. {ROLE_HINT[preview.role!]}
              </p>
            </div>
            {preview.projects && preview.projects.length > 0 && (
              <div className="block">
                {preview.projects.map((p) => (
                  <div key={p.prefix} className="row">
                    <SpaceDot space={p} />
                    <span className="t">
                      <span>{p.name}</span>
                    </span>
                    <span className="chip">{ROLE_LABEL[p.role]}</span>
                  </div>
                ))}
              </div>
            )}
            {error && (
              <p className="error-text" role="alert">
                {error}
              </p>
            )}
            {config?.roblox && (
              <a className="btn pri big-btn" href={`/auth/roblox/start?invite=${encodeURIComponent(token)}`}>
                Sign in with Roblox to join
              </a>
            )}
            {config?.devLogin && (
              <form className="block form" style={{ padding: 16 }} onSubmit={(e) => void devAccept(e)}>
                <span className="eyebrow">Developer sign-in · local only</span>
                <input className="input" placeholder="Handle" value={handle} onChange={(e) => setHandle(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))} aria-label="Handle" />
                <input className="input" placeholder="Display name" value={name} onChange={(e) => setName(e.target.value)} aria-label="Display name" />
                <button type="submit" className="btn" disabled={handle.length < 2}>
                  Join
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </main>
  );
}
